import { useState, useMemo } from 'react';
import { useProductStore, useCategoryStore, useCartStore } from '../../hooks';
import { useToast } from '@lancar-pos/ui';
import { ProductCard } from './ProductCard';
import { CategoryTabs } from './CategoryTabs';
import { SearchBar } from './SearchBar';
import { VariantSelector } from './VariantSelector';
import type { ProductVariant } from '@lancar-pos/db-schema';

export function ProductGrid() {
  const { products } = useProductStore();
  const { categories } = useCategoryStore();
  const { addItem } = useCartStore();
  const toast = useToast();
  
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => p.isActive);

    if (selectedCategoryId) {
      const childCategoryIds = categories
        .filter((c) => c.parentId === selectedCategoryId)
        .map((c) => c.id);
      
      const allCategoryIds = [selectedCategoryId, ...childCategoryIds];
      result = result.filter((p) => p.categoryId && allCategoryIds.includes(p.categoryId));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.sku?.toLowerCase().includes(query) ||
          p.barcode?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [products, selectedCategoryId, searchQuery, categories]);

  const selectedCategory = selectedCategoryId
    ? categories.find((c) => c.id === selectedCategoryId)
    : null;

  const handleProductClick = (product: typeof products[0]) => {
    // Check if product has active variants
    const hasVariants = product.variants && product.variants.some((v) => v.isActive);
    
    if (hasVariants) {
      setSelectedProduct(product);
    } else {
      // Check stock for products without variants
      if (product.stock <= 0) {
        toast.error('Produk habis');
        return;
      }
      addItem(product);
    }
  };

  const handleVariantSelect = (variant: ProductVariant) => {
    if (!selectedProduct) return;

    // Check variant stock
    if (variant.stock <= 0) {
      toast.error(`Varian ${variant.name} habis`);
      return;
    }

    // Add item with variant info
    addItem(selectedProduct, {
      id: variant.id,
      name: variant.name,
      price: selectedProduct.price + variant.priceModifier,
      stock: variant.stock,
    });
    
    toast.success(`Ditambahkan: ${selectedProduct.name} (${variant.name})`);
    setSelectedProduct(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="sticky top-0 bg-gray-50 z-10 pb-4 space-y-4">
        <SearchBar value={searchQuery} onChange={setSearchQuery} />
        <CategoryTabs
          selectedCategoryId={selectedCategoryId}
          onSelectCategory={setSelectedCategoryId}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <svg
              className="w-16 h-16 mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <p className="text-lg font-medium">Tidak ada produk</p>
            <p className="text-sm">
              {searchQuery
                ? `Tidak ditemukan produk "${searchQuery}"`
                : selectedCategory
                ? `Tidak ada produk di kategori "${selectedCategory.name}"`
                : 'Belum ada produk tersedia'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {filteredProducts.map((product) => {
              const category = categories.find(c => c.id === product.categoryId);
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  categoryColor={category?.color}
                  onClick={() => handleProductClick(product)}
                />
              );
            })}
          </div>
        )}
      </div>

      {selectedProduct && (
        <VariantSelector
          productName={selectedProduct.name}
          basePrice={selectedProduct.price}
          variants={selectedProduct.variants || []}
          onSelect={handleVariantSelect}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
